import {Injectable, Injector} from "@angular/core";
import {ReportError} from "app/app.service";

import {BaseListService, BatchRequest, BatchResponse} from "app/services/domain/base.service";
import * as Models from "app/services/proxy/model/models";
import {Lookup} from "framework/services/utils.service";

@Injectable()
export class ComplianceStandardsListService extends BaseListService<Models.ComplianceStandardListRequest, BatchRequest, Models.ComplianceStandardDetail>
{
    private readonly m_standardsByTenant: Lookup<Models.ComplianceStandardDetail[]>         = {};
    private readonly m_fetchingByTenant: Lookup<Promise<Models.ComplianceStandardDetail[]>> = {};

    constructor(inj: Injector)
    {
        super(inj, Models.ComplianceStandardDetail);
    }

    @ReportError
    public list(listReq: Models.ComplianceStandardListRequest): Promise<Models.ListResponse>
    {
        return this.domain.apis.compliance.listComplianceStandards(listReq) as Promise<Models.ListResponse>;
    }

    @ReportError
    protected async getBatchResponseImpl(req: BatchRequest): Promise<BatchResponse<Models.ComplianceStandardDetail>>
    {
        const standards                                       = await this.domain.apis.compliance.batchComplianceStandards(
            Models.ComplianceStandardBatchRequest.newInstance({
                                                                  compliance_standard_ids: this.getSysIds(req)
                                                              })
        );
        const lookup: Lookup<Models.ComplianceStandardDetail> = {};
        for (const std of standards || [])
        {
            lookup[std.compliance_standard_id] = std;
        }
        return {results: lookup};
    }

    protected createBatchRequest(listReq: Models.ComplianceStandardListRequest,
                                 ids: string[]): BatchRequest
    {
        return {ids};
    }

    public async getAllForTenant(tenantId: string): Promise<Models.ComplianceStandardDetail[]>
    {
        if (this.m_standardsByTenant[tenantId])
        {
            return this.m_standardsByTenant[tenantId];
        }

        if (!this.m_fetchingByTenant[tenantId])
        {
            this.m_fetchingByTenant[tenantId] = (async () =>
            {
                try
                {
                    const request                      = Models.ComplianceStandardListRequest.newInstance({
                                                                                                              list_context: Models.ListContext.newInstance({
                                                                                                                                                               batch_size  : 500,
                                                                                                                                                               batch_offset: 0
                                                                                                                                                           }),
                                                                                                              filters     : Models.ComplianceStandardFilter.newInstance({
                                                                                                                                                                            tenant_id: tenantId
                                                                                                                                                                        })
                                                                                                          });
                    const pairs                        = await this.getAll(request);
                    this.m_standardsByTenant[tenantId] = pairs?.map(([, std]) => std) || [];
                }
                finally
                {
                    delete this.m_fetchingByTenant[tenantId];
                }

                return this.m_standardsByTenant[tenantId];
            })();
        }

        return this.m_fetchingByTenant[tenantId];
    }

    public async getMarketplaceStandards(tenantId: string): Promise<Models.ComplianceStandardDetail[]>
    {
        const all = await this.getAllForTenant(tenantId);
        return all
            .filter(std => std.scope === Models.ComplianceStandardScope.global)
            .sort((a,
                   b) => (a.compliance_standard_name || "").localeCompare(b.compliance_standard_name || ""));
    }

    public async getCustomStandards(tenantId: string): Promise<Models.ComplianceStandardDetail[]>
    {
        const all = await this.getAllForTenant(tenantId);
        return all.filter(std => std.scope !== Models.ComplianceStandardScope.global);
    }

    public resetTenantCache(tenantId: string): void
    {
        this.m_standardsByTenant[tenantId] = null;
    }
}